import type { ReactNode } from "react";
import { useI18n, type Lang } from "@/lib/i18n";

/**
 * Three legal documents (Terms of Service, Privacy Policy,
 * Editorial & Takedown Policy) rendered verbatim per active locale.
 * Source: /docs (v1.0, 5 July 2026). Contact: editors@madcaptains.net.
 *
 * Multilingual content is branched by `lang` from useI18n(), mirroring
 * the pattern used in FoundersWord.tsx.
 */

type Doc = {
  id: "terms" | "privacy" | "editorial";
  title: string;
  effective: string;
  body: ReactNode;
};

type Labels = {
  onThisPage: string;
  contactLabel: string;
};

const LABELS: Record<Lang, Labels> = {
  en: { onThisPage: "On this page", contactLabel: "Contact" },
  ru: { onThisPage: "На этой странице", contactLabel: "Контакт" },
  fr: { onThisPage: "Sur cette page", contactLabel: "Contact" },
  es: { onThisPage: "En esta página", contactLabel: "Contacto" },
  zh: { onThisPage: "本页内容", contactLabel: "联系方式" },
  ar: { onThisPage: "في هذه الصفحة", contactLabel: "التواصل" },
};

const CONTACT_EMAIL = "editors@madcaptains.net";

function Mail() {
  return (
    <a
      href={`mailto:${CONTACT_EMAIL}`}
      className="underline-offset-4 hover:underline"
      style={{ color: "var(--amber)" }}
    >
      {CONTACT_EMAIL}
    </a>
  );
}

/* ------------------------------ ENGLISH ------------------------------- */
const DOCS_EN: Doc[] = [
  {
    id: "terms",
    title: "Terms of Service",
    effective: "Effective date: 5 July 2026",
    body: (
      <>
        <p>
          Welcome to MadCaptains.net (the &ldquo;Site&rdquo;). By accessing or using the Site, you agree to
          these Terms. If you do not agree, please do not use the Site.
        </p>
        <h3>1. What the Site is</h3>
        <p>
          The Site is a moderated, multilingual archive of anonymous first-person testimony from
          merchant seafarers about psychological pressure, harassment, and abuse of power aboard
          ships. It exists to reduce isolation (&ldquo;you are not alone&rdquo;) and to complement — never
          replace — official mechanisms (IMO, MLC 2006, flag-state and company complaint
          procedures, unions such as ITF).
        </p>
        <h3>2. What the Site is not</h3>
        <p>
          The Site is <strong>not</strong> a court, an investigative body, a legal service, a
          union, or a &ldquo;shame board.&rdquo; Entries are personal testimony, not verified findings of
          fact. The Site does not collect evidence for legal proceedings and makes no
          representation that any entry is complete, accurate, or suitable for any legal purpose.
        </p>
        <h3>3. Submissions</h3>
        <p>By submitting a story you confirm that:</p>
        <ul>
          <li>you are 18 years of age or older;</li>
          <li>
            the story describes your own experience (or, for &ldquo;confessor&rdquo; entries, your own
            conduct) in good faith;
          </li>
          <li>
            you have not included real names of individuals, vessel names, IMO numbers, company
            names, or combinations of details (port + date, etc.) that identify a specific person
            or vessel;
          </li>
          <li>
            you grant the Site a non-exclusive, worldwide, royalty-free licence to publish, edit
            for anonymity, translate, and display your submission in any language, in whole or in
            part, indefinitely. You retain authorship of your text. Because submissions are
            anonymous, entries cannot be reliably linked back to an author afterwards;
            publication should be considered permanent, subject to the Editorial &amp; Takedown
            Policy below.
          </li>
        </ul>
        <p>
          All submissions pass human pre-moderation. Moderators may edit solely to protect
          anonymity (with every edit logged), assign categories, or decline publication. Calls
          for violence, doxxing, or identification of individuals are removed or rejected.
        </p>
        <h3>4. Acceptable use</h3>
        <p>
          You agree not to: submit content you know to be false; attempt to identify authors or
          subjects of entries; use the Site to harass, defame, or threaten any person; scrape
          personal data; probe, disrupt, or attempt to bypass the Site&rsquo;s security or moderation.
        </p>
        <h3>5. Disclaimers</h3>
        <p>
          The Site and all content are provided &ldquo;AS IS&rdquo; and &ldquo;AS AVAILABLE,&rdquo; without warranties
          of any kind, express or implied. Testimony reflects the personal perception and memory
          of its author. To the maximum extent permitted by law, the Site&rsquo;s operator shall not
          be liable for any indirect, incidental, consequential, or punitive damages, or any
          loss of profits, data, or goodwill, arising from the use of or inability to use the
          Site. Where liability cannot be excluded, it is limited to the amount you paid to use
          the Site (the Site is free; that amount is zero).
        </p>
        <h3>6. Emergency notice</h3>
        <p>
          If you are in immediate danger at sea or ashore, contact your vessel&rsquo;s designated
          person ashore (DPA), your flag state, port authorities, or emergency services. See our
          /help page for practical steps. The Site is not an emergency service and is not
          monitored in real time.
        </p>
        <h3>7. Changes and contact</h3>
        <p>
          We may update these Terms; the effective date above will change accordingly. Continued
          use after changes constitutes acceptance. Questions and legal notices: via the contact
          given on the /legal page (<Mail />).
        </p>
        <h3>8. Governing law</h3>
        <p>
          These Terms are governed by the law of the operator&rsquo;s place of establishment within
          the European Union, without regard to conflict-of-law rules, and subject to any
          mandatory consumer protections of your place of residence.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    effective: "Effective date: 5 July 2026",
    body: (
      <>
        <p>Privacy is not a feature of this Site — it is its architecture.</p>
        <h3>1. What we deliberately do NOT collect</h3>
        <ul>
          <li>No accounts or registration for readers.</li>
          <li>
            No analytics, tracking pixels, or advertising identifiers on the submission page
            (/submit). No third-party trackers on the Site.
          </li>
          <li>
            No browser storage (cookies/localStorage) is used on /submit — the Site is designed
            for use from shared crew computers.
          </li>
          <li>
            We do not ask for, and ask you not to include, names of people, vessels, or
            companies in submissions.
          </li>
        </ul>
        <h3>2. What we process</h3>
        <ul>
          <li>
            <strong>Submission text and optional facets</strong> (e.g., decade, vessel type,
            abuser&rsquo;s position) — chosen by you, published after moderation.
          </li>
          <li>
            <strong>Optional contact (e-mail)</strong> — only if you choose to leave one for
            clarification requests. It is stored separately, visible only to the site
            administrator, never published, and <strong>never included in any AI processing</strong>.
          </li>
          <li>
            <strong>Technical server logs</strong> (IP addresses in standard web-server logs)
            are kept briefly for security and abuse prevention and are not linked to
            submissions.
          </li>
        </ul>
        <h3>3. AI processing</h3>
        <p>
          Submitted stories may be machine-translated into the Site&rsquo;s six languages and reviewed
          with AI assistance. AI systems receive the story text and facets only — never your
          contact details. A human moderator reviews and approves every publication and every
          translation. AI never publishes, rejects, or contacts anyone.
        </p>
        <h3>4. Legal bases and your rights (GDPR)</h3>
        <p>
          Where EU law applies, we process the minimal data above on the basis of legitimate
          interest (running a moderated public-interest archive) and, for an optional contact
          address, your consent. Because submissions are anonymous by design, we usually cannot
          link an entry to you and therefore cannot fulfil subject-access or erasure requests
          for published anonymous testimony; where you can reasonably demonstrate authorship or
          that content identifies you, see the Editorial &amp; Takedown Policy. You may lodge a
          complaint with your local data protection authority.
        </p>
        <h3>5. Storage and transfers</h3>
        <p>
          Data is hosted within the European Union. If you email us, that correspondence is
          handled by our e-mail provider under its own terms.
        </p>
        <h3>6. Children</h3>
        <p>
          The Site is intended for adults (18+). We do not knowingly accept submissions from
          minors.
        </p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "Editorial & Takedown Policy",
    effective: "Effective date: 5 July 2026",
    body: (
      <>
        <h3>1. Editorial principles</h3>
        <ol>
          <li>
            <strong>Testimony, not verdicts.</strong> Entries are personal accounts published
            for solidarity and awareness. The Site does not adjudicate facts.
          </li>
          <li>
            <strong>Anonymity by architecture.</strong> Before publication a human moderator
            checks every entry against a fixed checklist: no personal names, no vessel names or
            IMO numbers, no company names, no combinations of details that identify a person or
            vessel. Necessary redactions are made and logged.
          </li>
          <li>
            <strong>Voice preserved.</strong> Strong language is part of testimony and is not
            sanitised; only identifying details are edited. Quoted abusive speech is preserved
            as testimony.
          </li>
          <li>
            <strong>Original is canonical.</strong> Translations are AI-assisted and
            human-approved; each translation page shows the original language. In case of
            doubt, the original text prevails.
          </li>
          <li>
            <strong>What we reject:</strong> calls for violence, doxxing, threats, content
            identifying individuals or vessels, spam, and content unrelated to the Site&rsquo;s
            purpose.
          </li>
        </ol>
        <h3>2. Takedown and correction requests</h3>
        <p>
          Anyone may request review of a published entry — including people who believe an entry
          identifies them despite our safeguards, and authors who can reasonably demonstrate
          authorship.
        </p>
        <p>
          Send a request via the contact on the /legal page (<Mail />), including: the entry&rsquo;s
          URL, the specific text at issue, and the reason (identification risk, factual
          concern, authorship withdrawal, legal grounds).
        </p>
        <p>
          <strong>Our commitments:</strong>
        </p>
        <ul>
          <li>
            acknowledge within <strong>7 days</strong>; resolve within <strong>30 days</strong>{" "}
            where feasible;
          </li>
          <li>
            if content plausibly identifies a real person or vessel, we will redact or unpublish{" "}
            <strong>first</strong> and discuss second;
          </li>
          <li>
            authors who demonstrate authorship may request unpublication of their own entry; we
            honour such requests where verification is reasonably possible;
          </li>
          <li>we keep an internal log of takedown decisions.</li>
        </ul>
        <h3>3. Good-faith notice</h3>
        <p>
          The Site operates in good faith as a moderated archive of anonymous testimony. It does
          not knowingly host defamatory content: entries name no one, and identification of any
          person from a published entry would be contrary to both our rules and our editing
          process. If you believe an entry breaches this policy, tell us — that is what this
          process is for.
        </p>
      </>
    ),
  },
];

/* ------------------------------ RUSSIAN ------------------------------- */
const DOCS_RU: Doc[] = [
  {
    id: "terms",
    title: "Условия использования",
    effective: "Дата вступления в силу: 5 июля 2026 г.",
    body: (
      <>
        <p>
          Добро пожаловать на MadCaptains.net («Сайт»). Заходя на Сайт или пользуясь им, вы
          соглашаетесь с настоящими Условиями. Если вы не согласны — пожалуйста, не пользуйтесь
          Сайтом.
        </p>
        <h3>1. Что такое Сайт</h3>
        <p>
          Сайт — это модерируемый многоязычный архив анонимных свидетельств моряков торгового
          флота от первого лица о психологическом давлении, притеснениях и злоупотреблении
          властью на судах. Он существует, чтобы уменьшить изоляцию («ты не один»), и дополняет
          — но никогда не заменяет — официальные механизмы (ИМО, КТМС 2006, процедуры жалоб
          государства флага и компании, профсоюзы, такие как ITF).
        </p>
        <h3>2. Чем Сайт не является</h3>
        <p>
          Сайт <strong>не</strong> является судом, следственным органом, юридической службой,
          профсоюзом или «доской позора». Записи — это личные свидетельства, а не установленные
          факты. Сайт не собирает доказательства для юридических разбирательств и не заявляет,
          что какая-либо запись является полной, точной или пригодной для какой-либо юридической
          цели.
        </p>
        <h3>3. Присланные материалы</h3>
        <p>Присылая историю, вы подтверждаете, что:</p>
        <ul>
          <li>вам исполнилось 18 лет или больше;</li>
          <li>
            история описывает ваш собственный опыт (а для записей «исповедь» — ваши собственные
            действия) и изложена добросовестно;
          </li>
          <li>
            вы не указали настоящих имён людей, названий судов, номеров ИМО, названий компаний,
            а также сочетаний деталей (порт + дата и т. п.), позволяющих опознать конкретного
            человека или судно;
          </li>
          <li>
            вы предоставляете Сайту неисключительную, всемирную, безвозмездную лицензию на
            публикацию, редактирование ради анонимности, перевод и показ вашего материала на
            любом языке, полностью или частично, бессрочно. Авторство текста остаётся за вами.
            Поскольку материалы анонимны, записи впоследствии нельзя надёжно связать с автором;
            публикацию следует считать постоянной — с учётом Редакционной политики и политики
            удаления ниже.
          </li>
        </ul>
        <p>
          Все присланные материалы проходят премодерацию человеком. Модераторы могут
          редактировать текст исключительно ради защиты анонимности (каждая правка фиксируется в
          журнале), назначать категории или отклонять публикацию. Призывы к насилию, раскрытие
          личных данных (doxxing) и опознание отдельных лиц удаляются или отклоняются.
        </p>
        <h3>4. Допустимое использование</h3>
        <p>
          Вы соглашаетесь не: присылать заведомо ложный материал; пытаться опознать авторов или
          лиц из записей; использовать Сайт для травли, клеветы или угроз в адрес любого
          человека; собирать персональные данные скрейпингом; прощупывать, нарушать работу или
          пытаться обойти защиту и модерацию Сайта.
        </p>
        <h3>5. Отказ от гарантий</h3>
        <p>
          Сайт и всё его содержимое предоставляются «как есть» и «как доступно», без каких-либо
          гарантий, явных или подразумеваемых. Свидетельства отражают личное восприятие и память
          их авторов. В максимально допустимой законом степени оператор Сайта не несёт
          ответственности за любой косвенный, случайный, последующий или штрафной ущерб, а также
          за любую потерю прибыли, данных или деловой репутации, возникшую из-за использования
          Сайта или невозможности им пользоваться. Там, где ответственность не может быть
          исключена, она ограничивается суммой, которую вы заплатили за пользование Сайтом (Сайт
          бесплатен; эта сумма равна нулю).
        </p>
        <h3>6. Экстренное уведомление</h3>
        <p>
          Если вам угрожает непосредственная опасность в море или на берегу, обратитесь к
          назначенному лицу компании на берегу (DPA), в государство флага, к портовым властям
          или в экстренные службы. Практические шаги — на нашей странице /help. Сайт не является
          экстренной службой и не отслеживается в реальном времени.
        </p>
        <h3>7. Изменения и контакты</h3>
        <p>
          Мы можем обновлять настоящие Условия; дата вступления в силу выше будет меняться
          соответственно. Продолжение использования после изменений означает согласие. Вопросы и
          юридические уведомления — по контакту, указанному на странице /legal (<Mail />).
        </p>
        <h3>8. Применимое право</h3>
        <p>
          Настоящие Условия регулируются правом места учреждения оператора в пределах
          Европейского союза, без учёта коллизионных норм, и с сохранением любых обязательных
          норм защиты прав потребителей вашего места проживания.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Политика конфиденциальности",
    effective: "Дата вступления в силу: 5 июля 2026 г.",
    body: (
      <>
        <p>Конфиденциальность — не функция этого Сайта, а его архитектура.</p>
        <h3>1. Что мы сознательно НЕ собираем</h3>
        <ul>
          <li>Никаких аккаунтов и регистрации для читателей.</li>
          <li>
            Никакой аналитики, отслеживающих пикселей и рекламных идентификаторов на странице
            отправки (/submit). Никаких сторонних трекеров на Сайте.
          </li>
          <li>
            Никакого браузерного хранилища (cookies/localStorage) на /submit — Сайт рассчитан на
            работу с общих судовых компьютеров.
          </li>
          <li>
            Мы не просим — и просим вас не указывать — имён людей, названий судов или компаний в
            присылаемых материалах.
          </li>
        </ul>
        <h3>2. Что мы обрабатываем</h3>
        <ul>
          <li>
            <strong>Текст материала и необязательные метки</strong> (например, десятилетие, тип
            судна, должность обидчика) — выбираете вы, публикуются после модерации.
          </li>
          <li>
            <strong>Необязательный контакт (e-mail)</strong> — только если вы решите оставить
            его для уточняющих запросов. Он хранится отдельно, виден лишь администратору Сайта,
            никогда не публикуется и <strong>никогда не передаётся в обработку ИИ</strong>.
          </li>
          <li>
            <strong>Технические логи сервера</strong> (IP-адреса в стандартных журналах
            веб-сервера) хранятся недолго ради безопасности и предотвращения злоупотреблений и
            не связываются с присланными материалами.
          </li>
        </ul>
        <h3>3. Обработка с помощью ИИ</h3>
        <p>
          Присланные истории могут машинно переводиться на шесть языков Сайта и проходить
          проверку с помощью ИИ. Системы ИИ получают только текст истории и метки — никогда ваши
          контактные данные. Модератор-человек проверяет и одобряет каждую публикацию и каждый
          перевод. ИИ ничего не публикует, не отклоняет и ни с кем не связывается.
        </p>
        <h3>4. Правовые основания и ваши права (GDPR)</h3>
        <p>
          Там, где применимо право ЕС, мы обрабатываем минимальные данные, указанные выше, на
          основании законного интереса (ведение модерируемого общественно значимого архива), а
          для необязательного контактного адреса — на основании вашего согласия. Поскольку
          материалы анонимны по замыслу, мы обычно не можем связать запись с вами и потому не
          можем исполнить запросы на доступ к данным или их удаление в отношении опубликованных
          анонимных свидетельств; если вы можете обоснованно подтвердить авторство или то, что
          содержимое опознаёт вас, см. Редакционную политику и политику удаления. Вы вправе
          подать жалобу в местный орган по защите данных.
        </p>
        <h3>5. Хранение и передача</h3>
        <p>
          Данные размещаются в пределах Европейского союза. Если вы напишете нам на почту, эта
          переписка обрабатывается нашим почтовым провайдером на его собственных условиях.
        </p>
        <h3>6. Дети</h3>
        <p>
          Сайт предназначен для взрослых (18+). Мы сознательно не принимаем материалы от
          несовершеннолетних.
        </p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "Редакционная политика и политика удаления",
    effective: "Дата вступления в силу: 5 июля 2026 г.",
    body: (
      <>
        <h3>1. Редакционные принципы</h3>
        <ol>
          <li>
            <strong>Свидетельства, а не приговоры.</strong> Записи — это личные рассказы,
            публикуемые ради солидарности и осведомлённости. Сайт не устанавливает факты.
          </li>
          <li>
            <strong>Анонимность как архитектура.</strong> До публикации модератор-человек
            сверяет каждую запись с фиксированным чек-листом: никаких имён людей, никаких
            названий судов или номеров ИМО, никаких названий компаний, никаких сочетаний
            деталей, опознающих человека или судно. Необходимые изъятия вносятся и фиксируются в
            журнале.
          </li>
          <li>
            <strong>Голос сохраняется.</strong> Крепкие выражения — часть свидетельства и не
            сглаживаются; редактируются только опознающие детали. Приведённая грубая речь
            обидчика сохраняется как свидетельство.
          </li>
          <li>
            <strong>Оригинал каноничен.</strong> Переводы делаются с помощью ИИ и одобряются
            человеком; на каждой странице перевода указан язык оригинала. В случае сомнений
            преимущество имеет оригинальный текст.
          </li>
          <li>
            <strong>Что мы отклоняем:</strong> призывы к насилию, раскрытие личных данных,
            угрозы, содержимое, опознающее лиц или суда, спам, а также материалы, не связанные с
            целью Сайта.
          </li>
        </ol>
        <h3>2. Запросы на удаление и исправление</h3>
        <p>
          Любой может запросить пересмотр опубликованной записи — включая тех, кто считает, что
          запись опознаёт их вопреки нашим мерам, и авторов, способных обоснованно подтвердить
          авторство.
        </p>
        <p>
          Направьте запрос по контакту на странице /legal (<Mail />), указав: URL записи,
          конкретный спорный фрагмент и причину (риск опознания, фактическая претензия, отзыв
          авторства, юридические основания).
        </p>
        <p>
          <strong>Наши обязательства:</strong>
        </p>
        <ul>
          <li>
            подтвердить получение в течение <strong>7 дней</strong>; разрешить вопрос в течение{" "}
            <strong>30 дней</strong>, где это осуществимо;
          </li>
          <li>
            если содержимое правдоподобно опознаёт реального человека или судно, мы сначала
            изымем фрагмент или снимем запись с публикации, а обсуждать будем потом;
          </li>
          <li>
            авторы, подтвердившие авторство, могут запросить снятие своей записи с публикации;
            мы удовлетворяем такие запросы, где проверка разумно возможна;
          </li>
          <li>мы ведём внутренний журнал решений об удалении.</li>
        </ul>
        <h3>3. Уведомление о добросовестности</h3>
        <p>
          Сайт работает добросовестно как модерируемый архив анонимных свидетельств. Он
          сознательно не размещает клеветнического содержимого: записи никого не называют, а
          опознание любого человека по опубликованной записи противоречило бы и нашим правилам,
          и нашему процессу редактирования. Если вы считаете, что запись нарушает настоящую
          политику, сообщите нам — именно для этого и существует данный процесс.
        </p>
      </>
    ),
  },
];

/* ------------------------------ FRENCH -------------------------------- */
const DOCS_FR: Doc[] = [
  {
    id: "terms",
    title: "Conditions d'utilisation",
    effective: "Date d'entrée en vigueur : 5 juillet 2026",
    body: (
      <>
        <p>
          Bienvenue sur MadCaptains.net (le «&nbsp;Site&nbsp;»). En accédant au Site ou en
          l&rsquo;utilisant, vous acceptez les présentes Conditions. Si vous ne les acceptez pas,
          veuillez ne pas utiliser le Site.
        </p>
        <h3>1. Ce qu&rsquo;est le Site</h3>
        <p>
          Le Site est une archive modérée et multilingue de témoignages anonymes, à la première
          personne, de marins de la marine marchande, portant sur la pression psychologique, le
          harcèlement et l&rsquo;abus de pouvoir à bord des navires. Il existe pour réduire
          l&rsquo;isolement («&nbsp;vous n&rsquo;êtes pas seul&nbsp;») et pour compléter — jamais
          remplacer — les mécanismes officiels (OMI, MLC 2006, procédures de plainte de
          l&rsquo;État du pavillon et de la compagnie, syndicats tels que l&rsquo;ITF).
        </p>
        <h3>2. Ce que le Site n&rsquo;est pas</h3>
        <p>
          Le Site n&rsquo;est <strong>pas</strong> un tribunal, un organe d&rsquo;enquête, un
          service juridique, un syndicat ni un «&nbsp;pilori&nbsp;». Les entrées sont des
          témoignages personnels, non des faits établis. Le Site ne recueille pas de preuves
          destinées à des procédures judiciaires et ne garantit aucunement qu&rsquo;une entrée
          soit complète, exacte ou adaptée à une quelconque fin juridique.
        </p>
        <h3>3. Contributions</h3>
        <p>En envoyant un récit, vous confirmez que&nbsp;:</p>
        <ul>
          <li>vous êtes âgé de 18 ans ou plus&nbsp;;</li>
          <li>
            le récit décrit votre propre expérience (ou, pour les entrées «&nbsp;aveu&nbsp;»,
            votre propre conduite), de bonne foi&nbsp;;
          </li>
          <li>
            vous n&rsquo;avez pas inclus de noms réels de personnes, de noms de navires, de
            numéros OMI, de noms de sociétés, ni de combinaisons de détails (port + date, etc.)
            permettant d&rsquo;identifier une personne ou un navire précis&nbsp;;
          </li>
          <li>
            vous accordez au Site une licence non exclusive, mondiale et gratuite de publier, de
            modifier à des fins d&rsquo;anonymat, de traduire et d&rsquo;afficher votre
            contribution dans n&rsquo;importe quelle langue, en tout ou en partie, pour une
            durée indéfinie. Vous conservez la paternité de votre texte. Les contributions étant
            anonymes, les entrées ne peuvent ensuite être reliées de façon fiable à un
            auteur&nbsp;; la publication doit être considérée comme permanente, sous réserve de
            la Politique éditoriale et de retrait ci-dessous.
          </li>
        </ul>
        <p>
          Toutes les contributions font l&rsquo;objet d&rsquo;une pré-modération humaine. Les
          modérateurs peuvent éditer le texte uniquement pour protéger l&rsquo;anonymat (chaque
          modification étant journalisée), attribuer des catégories ou refuser la publication.
          Les appels à la violence, la divulgation de données personnelles (doxxing) et
          l&rsquo;identification de personnes sont supprimés ou rejetés.
        </p>
        <h3>4. Utilisation acceptable</h3>
        <p>
          Vous vous engagez à ne pas&nbsp;: soumettre un contenu que vous savez faux&nbsp;;
          tenter d&rsquo;identifier les auteurs ou les personnes évoquées dans les
          entrées&nbsp;; utiliser le Site pour harceler, diffamer ou menacer quiconque&nbsp;;
          extraire des données personnelles par moissonnage&nbsp;; sonder, perturber ou tenter
          de contourner la sécurité ou la modération du Site.
        </p>
        <h3>5. Exclusions de garantie</h3>
        <p>
          Le Site et l&rsquo;ensemble de son contenu sont fournis «&nbsp;EN L&rsquo;ÉTAT&nbsp;»
          et «&nbsp;SELON DISPONIBILITÉ&nbsp;», sans garantie d&rsquo;aucune sorte, expresse ou
          implicite. Les témoignages reflètent la perception et la mémoire personnelles de leur
          auteur. Dans toute la mesure permise par la loi, l&rsquo;exploitant du Site ne saurait
          être tenu responsable de tout dommage indirect, accessoire, consécutif ou punitif, ni
          d&rsquo;aucune perte de bénéfices, de données ou de réputation, résultant de
          l&rsquo;utilisation du Site ou de l&rsquo;impossibilité de l&rsquo;utiliser. Lorsque
          la responsabilité ne peut être exclue, elle est limitée au montant que vous avez payé
          pour utiliser le Site (le Site est gratuit&nbsp;; ce montant est nul).
        </p>
        <h3>6. Avertissement d&rsquo;urgence</h3>
        <p>
          Si vous êtes en danger immédiat, en mer ou à terre, contactez la personne désignée à
          terre (DPA) de votre navire, votre État du pavillon, les autorités portuaires ou les
          services d&rsquo;urgence. Consultez notre page /help pour les démarches pratiques. Le
          Site n&rsquo;est pas un service d&rsquo;urgence et n&rsquo;est pas surveillé en temps
          réel.
        </p>
        <h3>7. Modifications et contact</h3>
        <p>
          Nous pouvons mettre à jour les présentes Conditions&nbsp;; la date d&rsquo;entrée en
          vigueur ci-dessus sera modifiée en conséquence. La poursuite de l&rsquo;utilisation
          après modification vaut acceptation. Questions et notifications juridiques&nbsp;: via
          le contact indiqué sur la page /legal (<Mail />).
        </p>
        <h3>8. Droit applicable</h3>
        <p>
          Les présentes Conditions sont régies par le droit du lieu d&rsquo;établissement de
          l&rsquo;exploitant au sein de l&rsquo;Union européenne, sans égard aux règles de
          conflit de lois, et sous réserve des protections impératives des consommateurs de
          votre lieu de résidence.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Politique de confidentialité",
    effective: "Date d'entrée en vigueur : 5 juillet 2026",
    body: (
      <>
        <p>
          La confidentialité n&rsquo;est pas une fonctionnalité de ce Site — elle en est
          l&rsquo;architecture.
        </p>
        <h3>1. Ce que nous ne collectons délibérément PAS</h3>
        <ul>
          <li>Aucun compte ni inscription pour les lecteurs.</li>
          <li>
            Aucun outil d&rsquo;analyse, pixel de suivi ou identifiant publicitaire sur la page
            d&rsquo;envoi (/submit). Aucun traqueur tiers sur le Site.
          </li>
          <li>
            Aucun stockage de navigateur (cookies/localStorage) n&rsquo;est utilisé sur /submit
            — le Site est conçu pour être utilisé depuis des ordinateurs partagés de
            l&rsquo;équipage.
          </li>
          <li>
            Nous ne demandons pas, et nous vous demandons de ne pas inclure, de noms de
            personnes, de navires ou de sociétés dans les contributions.
          </li>
        </ul>
        <h3>2. Ce que nous traitons</h3>
        <ul>
          <li>
            <strong>Le texte de la contribution et les attributs facultatifs</strong> (par ex.
            décennie, type de navire, fonction de l&rsquo;auteur des faits) — choisis par vous,
            publiés après modération.
          </li>
          <li>
            <strong>Un contact facultatif (e-mail)</strong> — uniquement si vous choisissez
            d&rsquo;en laisser un pour des demandes de clarification. Il est stocké séparément,
            visible du seul administrateur du Site, jamais publié et{" "}
            <strong>jamais transmis à un quelconque traitement par IA</strong>.
          </li>
          <li>
            <strong>Les journaux techniques du serveur</strong> (adresses IP dans les journaux
            standard du serveur web) sont conservés brièvement à des fins de sécurité et de
            prévention des abus, et ne sont pas reliés aux contributions.
          </li>
        </ul>
        <h3>3. Traitement par IA</h3>
        <p>
          Les récits envoyés peuvent être traduits automatiquement dans les six langues du Site
          et examinés avec l&rsquo;aide de l&rsquo;IA. Les systèmes d&rsquo;IA ne reçoivent que
          le texte du récit et les attributs — jamais vos coordonnées. Un modérateur humain
          examine et approuve chaque publication et chaque traduction. L&rsquo;IA ne publie
          rien, ne rejette rien et ne contacte personne.
        </p>
        <h3>4. Bases légales et vos droits (RGPD)</h3>
        <p>
          Lorsque le droit de l&rsquo;UE s&rsquo;applique, nous traitons les données minimales
          ci-dessus sur la base de l&rsquo;intérêt légitime (l&rsquo;exploitation d&rsquo;une
          archive modérée d&rsquo;intérêt public) et, pour une adresse de contact facultative,
          de votre consentement. Les contributions étant anonymes par conception, nous ne
          pouvons généralement pas relier une entrée à vous et ne pouvons donc pas satisfaire
          les demandes d&rsquo;accès ou d&rsquo;effacement portant sur des témoignages anonymes
          publiés&nbsp;; lorsque vous pouvez raisonnablement démontrer la paternité ou que le
          contenu vous identifie, voyez la Politique éditoriale et de retrait. Vous pouvez
          déposer une réclamation auprès de votre autorité locale de protection des données.
        </p>
        <h3>5. Conservation et transferts</h3>
        <p>
          Les données sont hébergées au sein de l&rsquo;Union européenne. Si vous nous écrivez
          par e-mail, cette correspondance est traitée par notre fournisseur de messagerie selon
          ses propres conditions.
        </p>
        <h3>6. Enfants</h3>
        <p>
          Le Site est destiné aux adultes (18 ans et plus). Nous n&rsquo;acceptons pas sciemment
          de contributions de mineurs.
        </p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "Politique éditoriale et de retrait",
    effective: "Date d'entrée en vigueur : 5 juillet 2026",
    body: (
      <>
        <h3>1. Principes éditoriaux</h3>
        <ol>
          <li>
            <strong>Des témoignages, pas des verdicts.</strong> Les entrées sont des récits
            personnels publiés pour la solidarité et la sensibilisation. Le Site ne tranche pas
            les faits.
          </li>
          <li>
            <strong>L&rsquo;anonymat par l&rsquo;architecture.</strong> Avant publication, un
            modérateur humain vérifie chaque entrée selon une liste de contrôle fixe&nbsp;:
            aucun nom de personne, aucun nom de navire ni numéro OMI, aucun nom de société,
            aucune combinaison de détails identifiant une personne ou un navire. Les
            occultations nécessaires sont effectuées et journalisées.
          </li>
          <li>
            <strong>La voix préservée.</strong> Le langage cru fait partie du témoignage et
            n&rsquo;est pas édulcoré&nbsp;; seuls les détails identifiants sont modifiés. Les
            propos abusifs rapportés sont conservés en tant que témoignage.
          </li>
          <li>
            <strong>L&rsquo;original fait foi.</strong> Les traductions sont assistées par IA et
            approuvées par un humain&nbsp;; chaque page de traduction indique la langue
            d&rsquo;origine. En cas de doute, le texte original prévaut.
          </li>
          <li>
            <strong>Ce que nous rejetons&nbsp;:</strong> les appels à la violence, la
            divulgation de données personnelles, les menaces, tout contenu identifiant des
            personnes ou des navires, le spam, et tout contenu étranger à la vocation du Site.
          </li>
        </ol>
        <h3>2. Demandes de retrait et de correction</h3>
        <p>
          Toute personne peut demander le réexamen d&rsquo;une entrée publiée — y compris celles
          qui estiment qu&rsquo;une entrée les identifie malgré nos garde-fous, et les auteurs
          pouvant raisonnablement démontrer leur paternité.
        </p>
        <p>
          Envoyez une demande via le contact de la page /legal (<Mail />), en précisant&nbsp;:
          l&rsquo;URL de l&rsquo;entrée, le passage précis en cause et le motif (risque
          d&rsquo;identification, préoccupation factuelle, retrait de paternité, fondements
          juridiques).
        </p>
        <p>
          <strong>Nos engagements&nbsp;:</strong>
        </p>
        <ul>
          <li>
            accuser réception sous <strong>7 jours</strong>&nbsp;; résoudre sous{" "}
            <strong>30 jours</strong> lorsque cela est possible&nbsp;;
          </li>
          <li>
            si un contenu identifie de façon plausible une personne ou un navire réels, nous
            occultons ou dépublions <strong>d&rsquo;abord</strong>, et discutons ensuite&nbsp;;
          </li>
          <li>
            les auteurs qui démontrent leur paternité peuvent demander la dépublication de leur
            propre entrée&nbsp;; nous honorons ces demandes lorsque la vérification est
            raisonnablement possible&nbsp;;
          </li>
          <li>nous tenons un journal interne des décisions de retrait.</li>
        </ul>
        <h3>3. Avis de bonne foi</h3>
        <p>
          Le Site fonctionne de bonne foi comme une archive modérée de témoignages anonymes. Il
          n&rsquo;héberge pas sciemment de contenu diffamatoire&nbsp;: les entrées ne nomment
          personne, et l&rsquo;identification d&rsquo;une personne à partir d&rsquo;une entrée
          publiée serait contraire à la fois à nos règles et à notre processus d&rsquo;édition.
          Si vous estimez qu&rsquo;une entrée enfreint la présente politique, dites-le-nous —
          c&rsquo;est précisément la raison d&rsquo;être de ce processus.
        </p>
      </>
    ),
  },
];

/* ------------------------------ SPANISH ------------------------------- */
const DOCS_ES: Doc[] = [
  {
    id: "terms",
    title: "Términos de uso",
    effective: "Fecha de entrada en vigor: 5 de julio de 2026",
    body: (
      <>
        <p>
          Bienvenido a MadCaptains.net (el «Sitio»). Al acceder al Sitio o utilizarlo, usted
          acepta estos Términos. Si no está de acuerdo, le rogamos que no utilice el Sitio.
        </p>
        <h3>1. Qué es el Sitio</h3>
        <p>
          El Sitio es un archivo moderado y multilingüe de testimonios anónimos, en primera
          persona, de marinos de la marina mercante, sobre presión psicológica, acoso y abuso
          de poder a bordo de los buques. Existe para reducir el aislamiento («no estás solo»)
          y para complementar —nunca sustituir— los mecanismos oficiales (OMI, MLC 2006,
          procedimientos de queja del Estado del pabellón y de la empresa, sindicatos como la
          ITF).
        </p>
        <h3>2. Qué no es el Sitio</h3>
        <p>
          El Sitio <strong>no</strong> es un tribunal, un órgano de investigación, un servicio
          jurídico, un sindicato ni un «muro de la vergüenza». Las entradas son testimonios
          personales, no hechos probados. El Sitio no recopila pruebas para procedimientos
          judiciales y no garantiza que ninguna entrada sea completa, exacta o adecuada para
          fin jurídico alguno.
        </p>
        <h3>3. Aportaciones</h3>
        <p>Al enviar un relato, usted confirma que:</p>
        <ul>
          <li>tiene 18 años de edad o más;</li>
          <li>
            el relato describe su propia experiencia (o, en las entradas de tipo «confesión»,
            su propia conducta), de buena fe;
          </li>
          <li>
            no ha incluido nombres reales de personas, nombres de buques, números OMI, nombres
            de empresas ni combinaciones de datos (puerto + fecha, etc.) que identifiquen a
            una persona o un buque concretos;
          </li>
          <li>
            concede al Sitio una licencia no exclusiva, mundial y gratuita para publicar,
            editar con fines de anonimato, traducir y mostrar su aportación en cualquier
            idioma, total o parcialmente, de forma indefinida. Usted conserva la autoría de su
            texto. Dado que las aportaciones son anónimas, las entradas no pueden vincularse
            después de forma fiable a un autor; la publicación debe considerarse permanente,
            con sujeción a la Política editorial y de retirada que figura más abajo.
          </li>
        </ul>
        <p>
          Todas las aportaciones pasan por una premoderación humana. Los moderadores pueden
          editar el texto únicamente para proteger el anonimato (registrando cada cambio),
          asignar categorías o rechazar la publicación. Las incitaciones a la violencia, la
          divulgación de datos personales (doxxing) y la identificación de personas se
          eliminan o se rechazan.
        </p>
        <h3>4. Uso aceptable</h3>
        <p>
          Usted se compromete a no: enviar contenido que sepa que es falso; intentar
          identificar a los autores o a las personas aludidas en las entradas; utilizar el
          Sitio para acosar, difamar o amenazar a nadie; extraer datos personales mediante
          scraping; sondear, perturbar o intentar eludir la seguridad o la moderación del
          Sitio.
        </p>
        <h3>5. Exención de garantías</h3>
        <p>
          El Sitio y todo su contenido se ofrecen «TAL CUAL» y «SEGÚN DISPONIBILIDAD», sin
          garantías de ningún tipo, expresas o implícitas. Los testimonios reflejan la
          percepción y la memoria personales de su autor. En la máxima medida permitida por la
          ley, el operador del Sitio no será responsable de ningún daño indirecto, incidental,
          consecuente o punitivo, ni de pérdida alguna de beneficios, datos o reputación,
          derivada del uso del Sitio o de la imposibilidad de usarlo. Cuando la
          responsabilidad no pueda excluirse, se limita al importe que usted haya pagado por
          usar el Sitio (el Sitio es gratuito; ese importe es cero).
        </p>
        <h3>6. Aviso de emergencia</h3>
        <p>
          Si se encuentra en peligro inmediato, en el mar o en tierra, contacte con la persona
          designada en tierra (DPA) de su buque, con su Estado del pabellón, con las
          autoridades portuarias o con los servicios de emergencia. Consulte nuestra página
          /help para conocer los pasos prácticos. El Sitio no es un servicio de emergencia y
          no se supervisa en tiempo real.
        </p>
        <h3>7. Cambios y contacto</h3>
        <p>
          Podemos actualizar estos Términos; la fecha de entrada en vigor anterior cambiará en
          consecuencia. El uso continuado tras los cambios constituye aceptación. Consultas y
          notificaciones legales: a través del contacto indicado en la página /legal (<Mail />).
        </p>
        <h3>8. Ley aplicable</h3>
        <p>
          Estos Términos se rigen por la ley del lugar de establecimiento del operador dentro
          de la Unión Europea, sin atender a las normas de conflicto de leyes, y con sujeción a
          las protecciones imperativas de los consumidores de su lugar de residencia.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "Política de privacidad",
    effective: "Fecha de entrada en vigor: 5 de julio de 2026",
    body: (
      <>
        <p>La privacidad no es una función de este Sitio: es su arquitectura.</p>
        <h3>1. Lo que deliberadamente NO recopilamos</h3>
        <ul>
          <li>Ninguna cuenta ni registro para los lectores.</li>
          <li>
            Ninguna analítica, píxel de seguimiento ni identificador publicitario en la página
            de envío (/submit). Ningún rastreador de terceros en el Sitio.
          </li>
          <li>
            No se utiliza almacenamiento del navegador (cookies/localStorage) en /submit: el
            Sitio está pensado para usarse desde ordenadores compartidos de la tripulación.
          </li>
          <li>
            No pedimos —y le pedimos que no incluya— nombres de personas, buques o empresas en
            las aportaciones.
          </li>
        </ul>
        <h3>2. Lo que tratamos</h3>
        <ul>
          <li>
            <strong>El texto de la aportación y los atributos opcionales</strong> (p. ej.,
            década, tipo de buque, cargo de la persona que cometió los hechos): elegidos por
            usted, publicados tras la moderación.
          </li>
          <li>
            <strong>Un contacto opcional (correo electrónico)</strong>: solo si decide dejar
            uno para solicitudes de aclaración. Se almacena por separado, visible únicamente
            para el administrador del Sitio, nunca se publica y{" "}
            <strong>nunca se incluye en ningún tratamiento por IA</strong>.
          </li>
          <li>
            <strong>Los registros técnicos del servidor</strong> (direcciones IP en los
            registros estándar del servidor web) se conservan brevemente por seguridad y
            prevención de abusos, y no se vinculan a las aportaciones.
          </li>
        </ul>
        <h3>3. Tratamiento por IA</h3>
        <p>
          Los relatos enviados pueden traducirse automáticamente a los seis idiomas del Sitio y
          revisarse con ayuda de IA. Los sistemas de IA reciben únicamente el texto del relato
          y los atributos, nunca sus datos de contacto. Un moderador humano revisa y aprueba
          cada publicación y cada traducción. La IA no publica, no rechaza ni contacta con
          nadie.
        </p>
        <h3>4. Bases jurídicas y sus derechos (RGPD)</h3>
        <p>
          Cuando resulte aplicable el Derecho de la UE, tratamos los datos mínimos anteriores
          sobre la base del interés legítimo (la gestión de un archivo moderado de interés
          público) y, para una dirección de contacto opcional, de su consentimiento. Dado que
          las aportaciones son anónimas por diseño, por lo general no podemos vincular una
          entrada con usted y, por tanto, no podemos atender solicitudes de acceso o supresión
          respecto de testimonios anónimos publicados; cuando usted pueda demostrar
          razonablemente la autoría o que el contenido lo identifica, consulte la Política
          editorial y de retirada. Puede presentar una reclamación ante su autoridad local de
          protección de datos.
        </p>
        <h3>5. Conservación y transferencias</h3>
        <p>
          Los datos se alojan dentro de la Unión Europea. Si nos escribe por correo
          electrónico, esa correspondencia la gestiona nuestro proveedor de correo con arreglo
          a sus propias condiciones.
        </p>
        <h3>6. Menores</h3>
        <p>
          El Sitio está destinado a personas adultas (mayores de 18 años). No aceptamos
          conscientemente aportaciones de menores.
        </p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "Política editorial y de retirada",
    effective: "Fecha de entrada en vigor: 5 de julio de 2026",
    body: (
      <>
        <h3>1. Principios editoriales</h3>
        <ol>
          <li>
            <strong>Testimonios, no veredictos.</strong> Las entradas son relatos personales
            publicados en aras de la solidaridad y la concienciación. El Sitio no dirime los
            hechos.
          </li>
          <li>
            <strong>Anonimato por arquitectura.</strong> Antes de la publicación, un moderador
            humano coteja cada entrada con una lista de comprobación fija: ningún nombre de
            persona, ningún nombre de buque ni número OMI, ningún nombre de empresa, ninguna
            combinación de datos que identifique a una persona o un buque. Las supresiones
            necesarias se realizan y se registran.
          </li>
          <li>
            <strong>La voz se preserva.</strong> El lenguaje fuerte forma parte del testimonio
            y no se suaviza; solo se editan los detalles identificativos. El habla abusiva
            citada se conserva como testimonio.
          </li>
          <li>
            <strong>El original es canónico.</strong> Las traducciones se asisten con IA y las
            aprueba una persona; cada página de traducción indica el idioma original. En caso
            de duda, prevalece el texto original.
          </li>
          <li>
            <strong>Lo que rechazamos:</strong> incitaciones a la violencia, divulgación de
            datos personales, amenazas, contenido que identifique a personas o buques, spam y
            contenido ajeno al propósito del Sitio.
          </li>
        </ol>
        <h3>2. Solicitudes de retirada y corrección</h3>
        <p>
          Cualquiera puede solicitar la revisión de una entrada publicada, incluidas las
          personas que crean que una entrada las identifica pese a nuestras salvaguardas, y
          los autores que puedan demostrar razonablemente su autoría.
        </p>
        <p>
          Envíe una solicitud a través del contacto de la página /legal (<Mail />), indicando:
          la URL de la entrada, el pasaje concreto en cuestión y el motivo (riesgo de
          identificación, preocupación factual, retirada de autoría, fundamentos jurídicos).
        </p>
        <p>
          <strong>Nuestros compromisos:</strong>
        </p>
        <ul>
          <li>
            acusar recibo en un plazo de <strong>7 días</strong>; resolver en un plazo de{" "}
            <strong>30 días</strong> cuando sea factible;
          </li>
          <li>
            si un contenido identifica de forma plausible a una persona o un buque reales,{" "}
            <strong>primero</strong> suprimimos o despublicamos, y discutimos después;
          </li>
          <li>
            los autores que demuestren su autoría pueden solicitar la despublicación de su
            propia entrada; atendemos tales solicitudes cuando la verificación es
            razonablemente posible;
          </li>
          <li>mantenemos un registro interno de las decisiones de retirada.</li>
        </ul>
        <h3>3. Aviso de buena fe</h3>
        <p>
          El Sitio opera de buena fe como un archivo moderado de testimonios anónimos. No
          aloja conscientemente contenido difamatorio: las entradas no nombran a nadie, y la
          identificación de cualquier persona a partir de una entrada publicada sería
          contraria tanto a nuestras normas como a nuestro proceso de edición. Si cree que una
          entrada infringe esta política, díganoslo: para eso existe precisamente este
          proceso.
        </p>
      </>
    ),
  },
];

/* ------------------------------ CHINESE ------------------------------- */
const DOCS_ZH: Doc[] = [
  {
    id: "terms",
    title: "使用条款",
    effective: "生效日期：2026年7月5日",
    body: (
      <>
        <p>
          欢迎访问 MadCaptains.net（“本网站”）。访问或使用本网站即表示您同意本条款。如不同意，请勿使用本网站。
        </p>
        <h3>1. 本网站的性质</h3>
        <p>
          本网站是一个经过审核、多语言、匿名的第一手证言存档库，证言来自商船海员，关于船上的心理压力、骚扰和权力滥用。本网站旨在减少孤立感（“你并不孤单”），并补充——绝非取代——官方机制（IMO、MLC 2006、船旗国和公司投诉程序、工会如 ITF）。
        </p>
        <h3>2. 本网站不是什么</h3>
        <p>
          本网站不是法院、调查机构、法律服务、工会，也不是“羞辱榜”或点名曝光、羞辱个人的平台。所发布的内容均为个人证言，而非经核实的事实认定。本网站不为法律程序收集证据，亦不声称任何证言完整、准确或适用于任何法律目的。
        </p>
        <h3>3. 提交内容</h3>
        <p>提交故事即表示您确认：</p>
        <ul>
          <li>您已年满18周岁；</li>
          <li>该故事是您出于善意对自己亲身经历的描述（或就“自白者”条目而言，为您自己行为的描述）；</li>
          <li>您未包含任何个人的真实姓名、船舶名称、IMO编号、公司名称，或可指向特定人员或船舶的细节组合（如港口+日期等）；</li>
          <li>
            您授予本网站一项非排他性、全球性、免版税的许可，以便无限期地以任何语言发布、编辑（为匿名）、翻译和展示您的提交内容（全部或部分）。您保留您文本的作者身份。由于提交是匿名的，之后无法将条目可靠地关联回作者；发布应视为永久性的，但须遵守下文编辑与撤下政策。
          </li>
        </ul>
        <p>
          所有提交均经过人工预审。审核员仅可为保护匿名而进行编辑（每次编辑均有记录）、分配类别或拒绝发布。呼吁暴力、公开他人身份信息（doxxing）或识别个人身份的内容将被删除或拒绝。
        </p>
        <h3>4. 可接受的使用</h3>
        <p>
          您同意不：提交您明知为虚假的内容；试图识别条目作者或当事人；使用本网站骚扰、诽谤或威胁任何人；抓取个人数据；探测、破坏或试图绕过本网站的安全或审核机制。
        </p>
        <h3>5. 免责声明</h3>
        <p>
          本网站及其所有内容均按“现状”和“现有”提供，不作任何明示或暗示的保证。证言仅反映作者的个人认知和记忆。在法律允许的最大范围内，本网站运营者不对因使用或无法使用本网站而引起的任何间接、偶然、后果性或惩罚性损害，或任何利润、数据或商誉损失承担责任。若无法排除责任，则责任限于您为使用本网站所支付的金额（本网站免费，故金额为零）。
        </p>
        <h3>6. 紧急情况提示</h3>
        <p>
          如果您在海上或岸上处于即时危险中，请联系您船舶的岸上指定人员（DPA）、船旗国、港口当局或紧急服务部门。请参阅我们的 /help 页面了解实际步骤。本网站不是紧急服务部门，也不进行实时监控。
        </p>
        <h3>7. 变更与联系方式</h3>
        <p>
          我们可能会更新本条款；上述生效日期将相应更改。变更后继续使用即表示接受。疑问和法律通知：通过 /legal 页面上提供的联系方式发送（<Mail />）。
        </p>
        <h3>8. 管辖法律</h3>
        <p>
          本条款受运营者在欧盟内设立地的法律管辖，不考虑冲突法规则，并受您居住地任何强制性消费者保护规定的约束。
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "隐私政策",
    effective: "生效日期：2026年7月5日",
    body: (
      <>
        <p>隐私不是本网站的一项功能——而是其架构本身。</p>
        <h3>1. 我们刻意不收集的内容</h3>
        <ul>
          <li>读者无需账户或注册。</li>
          <li>提交页面（/submit）无分析工具、追踪像素或广告标识符。本网站无第三方追踪器。</li>
          <li>提交页面（/submit）不使用浏览器存储（cookie/localStorage）——本网站设计为可在共享船员电脑上使用。</li>
          <li>我们不要求、也请您不要在提交内容中包含人名、船舶名称或公司名称。</li>
        </ul>
        <h3>2. 我们处理的内容</h3>
        <ul>
          <li><strong>提交文本及可选分类信息</strong>（例如年代、船舶类型、施暴者职位）——由您选择，经审核后发布。</li>
          <li><strong>可选联系方式（电子邮件）</strong>——仅在您选择留下以供澄清请求时使用。该信息分开存储，仅网站管理员可见，绝不公开，且绝不纳入任何人工智能处理。</li>
          <li><strong>技术服务器日志</strong>（标准 Web 服务器日志中的 IP 地址）会短暂保留，用于安全和防滥用目的，且不与提交内容关联。</li>
        </ul>
        <h3>3. 人工智能处理</h3>
        <p>
          提交的故事可能会被机器翻译为本网站的六种语言，并在人工智能辅助下进行审核。人工智能系统仅接收故事文本和分类信息——绝不接收您的联系方式。人工审核员审核并批准每篇发布及每篇翻译。人工智能从不发布、拒绝或联系任何人。
        </p>
        <h3>4. 法律依据与您的权利（GDPR）</h3>
        <p>
          在欧盟法律适用的情况下，我们基于正当利益（运营一个经审核的公共利益存档库）处理上述最少数据，对于可选的联系方式则基于您的同意。由于提交内容在设计上是匿名的，我们通常无法将条目关联到您，因此无法对已发布的匿名证言履行主体访问或删除请求；如您可以合理证明作者身份或内容指向您，请参阅编辑与撤下政策。您可以向当地数据保护机构提出投诉。
        </p>
        <h3>5. 存储与传输</h3>
        <p>数据托管于欧盟境内。如您发送电子邮件给我们，该通信由我们的电子邮件服务提供商依据其自身条款处理。</p>
        <h3>6. 儿童</h3>
        <p>本网站面向成年人（18岁以上）。我们不会在知情的情况下接受未成年人的提交。</p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "编辑与撤下政策",
    effective: "生效日期：2026年7月5日",
    body: (
      <>
        <h3>1. 编辑原则</h3>
        <ol>
          <li><strong>证言，而非裁决。</strong> 所发布内容为个人叙述，旨在团结与提高认知。本网站不对事实作出裁定。</li>
          <li><strong>架构性匿名。</strong> 发布前，人工审核员依据固定核对清单检查每篇条目：无个人姓名、无船舶名称或 IMO 编号、无公司名称、无指向人员或船舶的细节组合。进行必要的删节并记录。</li>
          <li><strong>保留原声。</strong> 强烈言辞是证言的一部分，不予净化处理；仅编辑可识别身份的细节。引用的辱骂性言论作为证言予以保留。</li>
          <li><strong>原文为规范版本。</strong> 翻译由人工智能辅助并经人工批准；每个翻译页面均显示原文语言。如有疑义，以原文为准。</li>
          <li><strong>我们拒绝的内容：</strong> 呼吁暴力、公开他人身份信息（doxxing）、威胁、识别个人或船舶的内容、垃圾信息，以及与本网站目的无关的内容。</li>
        </ol>
        <h3>2. 撤下与更正请求</h3>
        <p>
          任何人可请求复核已发布的条目——包括认为尽管有我们的保障措施但仍被条目识别出的人，以及能合理证明作者身份的作者。
        </p>
        <p>
          请通过 /legal 页面上的联系方式发送请求（<Mail />），包括：条目 URL、有争议的具体文本，以及理由（识别风险、事实关切、作者撤回、法律依据）。
        </p>
        <p><strong>我们的承诺：</strong></p>
        <ul>
          <li>在 <strong>7天</strong> 内确认收到；在可行的情况下于 <strong>30天</strong> 内解决；</li>
          <li>如内容看似可识别真实人员或船舶，我们将先进行删节或取消发布，再进行讨论；</li>
          <li>能证明作者身份的作者可请求取消发布自己的条目；在合理可验证的情况下，我们将尊重此类请求；</li>
          <li>我们内部保留撤下决定的日志。</li>
        </ul>
        <h3>3. 善意声明</h3>
        <p>
          本网站作为匿名证言的经审核存档库，基于善意运营。本网站不会在知情情况下托管诽谤性内容：条目不指名任何人，从已发布条目中识别任何人与我们的规则和编辑流程均相违背。如您认为某条目违反本政策，请告知我们——这正是此流程的目的所在。
        </p>
      </>
    ),
  },
];

/* ------------------------------- ARABIC ------------------------------- */
const DOCS_AR: Doc[] = [
  {
    id: "terms",
    title: "شروط الاستخدام",
    effective: "تاريخ السريان: 5 يوليو 2026",
    body: (
      <>
        <p>
          مرحباً بكم في MadCaptains.net (ويُشار إليه فيما يلي بـ &laquo;الموقع&raquo;). بالوصول إلى الموقع أو استخدامه، فإنك توافق على هذه الشروط. إذا كنت لا توافق عليها، فيُرجى عدم استخدام الموقع.
        </p>
        <h3>1. طبيعة الموقع</h3>
        <p>
          الموقع أرشيف مُدار ومُراجَع، متعدد اللغات، يضم شهادات شخصية مجهولة من بحارة السفن التجارية بشأن الضغط النفسي، والتحرش، وإساءة استعمال السلطة على متن السفن. والغرض منه تقليل الشعور بالعزلة (&laquo;لست وحدك&raquo;) ودعم الآليات الرسمية — لا الحلول محلها أبداً — مثل المنظمة البحرية الدولية (IMO)، واتفاقية العمل البحري لعام 2006 (MLC 2006)، وإجراءات الشكاوى لدى دولة العلم والشركات، والنقابات مثل الاتحاد الدولي لعمال النقل (ITF).
        </p>
        <h3>2. ما لا يمثله الموقع</h3>
        <p>
          الموقع <strong>ليس</strong> محكمة، ولا هيئة تحقيق، ولا خدمة قانونية، ولا نقابة، ولا &laquo;لوحة تشهير&raquo;. المواد المنشورة هي شهادات شخصية، وليست نتائج مثبتة للتحقيق في الوقائع. ولا يجمع الموقع أدلة للإجراءات القانونية، ولا يقدم أي إقرار بأن أي مادة منشورة كاملة أو دقيقة أو مناسبة لأي غرض قانوني.
        </p>
        <h3>3. المواد المُرسلة</h3>
        <p>بإرسال قصة، فإنك تؤكد ما يلي:</p>
        <ul>
          <li>أنك بلغت 18 عاماً أو أكثر؛</li>
          <li>أن القصة تصف تجربتك الشخصية أنت (أو، بالنسبة إلى إدخالات &laquo;المُقرّ&raquo;، سلوكك أنت) بحسن نية؛</li>
          <li>أنك لم تُدرج أسماء حقيقية لأفراد، ولا أسماء سفن، ولا أرقام IMO، ولا أسماء شركات، ولا تركيبات من التفاصيل (ميناء + تاريخ، إلخ) يمكن أن تكشف هوية شخص أو سفينة محددة؛</li>
          <li>أنك تمنح الموقع ترخيصاً غير حصري، عالمياً، وخالياً من الرسوم والإتاوات، لنشر مادتك، وتحريرها لأغراض إخفاء الهوية، وترجمتها، وعرضها بأي لغة، كلياً أو جزئياً، إلى أجل غير مسمى. وتحتفظ أنت بصفة المؤلف لنصك. وبما أن المواد تُرسل بصورة مجهولة، فلا يمكن لاحقاً ربطها بمؤلفها على نحو موثوق؛ ولذلك ينبغي اعتبار النشر دائماً، مع مراعاة السياسة التحريرية وسياسة الإزالة أدناه.</li>
        </ul>
        <p>
          تمر جميع المواد بمراجعة بشرية مسبقة. ويجوز للمراجعين التحرير حصراً لحماية إخفاء الهوية (مع تسجيل كل تعديل)، أو تعيين الفئات، أو رفض النشر. وتُزال أو تُرفض الدعوات إلى العنف، أو كشف بيانات هوية الآخرين (doxxing)، أو تحديد هوية الأفراد.
        </p>
        <h3>4. الاستخدام المقبول</h3>
        <p>
          توافق على ألا تقوم بما يلي: إرسال محتوى تعلم أنه كاذب؛ محاولة تحديد هوية مؤلفي المواد أو الأشخاص المعنيين بها؛ استخدام الموقع لمضايقة أي شخص أو التشهير به أو تهديده؛ استخراج البيانات الشخصية؛ اختبار أمن الموقع أو نظمه، أو تعطيلها، أو محاولة تجاوزها أو تجاوز آليات المراجعة.
        </p>
        <h3>5. إخلاء المسؤولية</h3>
        <p>
          يُقدَّم الموقع وجميع محتوياته على أساس &laquo;كما هو&raquo; و&laquo;كما هو متاح&raquo;، من دون أي ضمانات من أي نوع، صريحة كانت أم ضمنية. وتعكس الشهادة الإدراك الشخصي والذاكرة الشخصية لمؤلفها. وإلى أقصى حد يسمح به القانون، لا يتحمل مشغّل الموقع أي مسؤولية عن أي أضرار غير مباشرة أو عرضية أو تبعية أو عقابية، أو عن أي خسارة في الأرباح أو البيانات أو السمعة المعنوية، تنشأ عن استخدام الموقع أو عدم القدرة على استخدامه. وحيث لا يمكن استبعاد المسؤولية، فإنها تقتصر على المبلغ الذي دفعته لاستخدام الموقع (الموقع مجاني؛ ولذلك فالمبلغ هو صفر).
        </p>
        <h3>6. إشعار الطوارئ</h3>
        <p>
          إذا كنت في خطر فوري في البحر أو على البر، فاتصل بالشخص المعيّن على البر (DPA) الخاص بسفينتك، أو بدولة العلم، أو بسلطات الميناء، أو بخدمات الطوارئ. راجع صفحة /help للاطلاع على خطوات عملية. الموقع ليس خدمة طوارئ ولا تتم مراقبته في الوقت الفعلي.
        </p>
        <h3>7. التغييرات ووسائل الاتصال</h3>
        <p>
          يجوز لنا تحديث هذه الشروط؛ وسيتغير تاريخ السريان أعلاه وفقاً لذلك. ويُعد استمرارك في الاستخدام بعد التغييرات قبولاً لها. تُرسل الأسئلة والإشعارات القانونية عبر وسيلة الاتصال المذكورة في صفحة /legal (<Mail />).
        </p>
        <h3>8. القانون الواجب التطبيق</h3>
        <p>
          تخضع هذه الشروط لقانون مقرّ مشغّل الموقع داخل الاتحاد الأوروبي، من دون إعمال قواعد تنازع القوانين، ومع عدم الإخلال بأي حماية إلزامية للمستهلكين في مكان إقامتك.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    title: "سياسة الخصوصية",
    effective: "تاريخ السريان: 5 يوليو 2026",
    body: (
      <>
        <p>الخصوصية ليست مجرد ميزة في هذا الموقع — بل هي بنيته الأساسية.</p>
        <h3>1. ما نتعمد عدم جمعه</h3>
        <ul>
          <li>لا توجد حسابات أو تسجيل للقرّاء.</li>
          <li>لا توجد أدوات تحليل، ولا بيكسلات تتبع، ولا معرّفات إعلانية في صفحة الإرسال (/submit). ولا توجد متتبعات خارجية على الموقع.</li>
          <li>لا يُستخدم أي تخزين في المتصفح (cookies/localStorage) في /submit — فقد صُمم الموقع للاستخدام من أجهزة الحواسيب المشتركة الخاصة بالطاقم.</li>
          <li>لا نطلب منك، بل نطلب منك ألا تُدرج، أسماء أشخاص أو سفن أو شركات في المواد التي ترسلها.</li>
        </ul>
        <h3>2. ما نعالجه</h3>
        <ul>
          <li><strong>نص المادة والحقول الاختيارية</strong> (مثل: العقد الزمني، نوع السفينة، صفة الشخص المسيء أو منصبه) — تختارها أنت، وتُنشر بعد المراجعة.</li>
          <li><strong>وسيلة اتصال اختيارية (البريد الإلكتروني)</strong> — فقط إذا اخترت تركها لطلبات التوضيح. تُخزَّن هذه الوسيلة منفصلة، ولا يراها إلا مسؤول الموقع، ولا تُنشر أبداً، و<strong>لا تُدرج أبداً في أي معالجة بالذكاء الاصطناعي</strong>.</li>
          <li><strong>سجلات الخادم التقنية</strong> (عناوين IP في سجلات خادم الويب القياسية) — يُحتفظ بها لفترة وجيزة لأغراض الأمن ومنع إساءة الاستخدام، ولا تُربط بالمواد المرسلة.</li>
        </ul>
        <h3>3. معالجة الذكاء الاصطناعي</h3>
        <p>
          قد تُترجم القصص المرسلة آلياً إلى لغات الموقع الست، وقد تُراجع بمساعدة الذكاء الاصطناعي. وتتلقى أنظمة الذكاء الاصطناعي نص القصة والحقول الاختيارية فقط — ولا تتلقى تفاصيل اتصالك أبداً. ويراجع مشرف بشري كل منشور وكل ترجمة ويوافق عليهما. ولا يقوم الذكاء الاصطناعي أبداً بالنشر أو الرفض أو الاتصال بأي شخص.
        </p>
        <h3>4. الأسس القانونية وحقوقك (GDPR)</h3>
        <p>
          حيثما ينطبق قانون الاتحاد الأوروبي، فإننا نعالج الحد الأدنى من البيانات المذكورة أعلاه على أساس المصلحة المشروعة (تشغيل أرشيف مُراجع يخدم المصلحة العامة)، وبالنسبة إلى عنوان الاتصال الاختياري، على أساس موافقتك. وبما أن المواد مجهولة بطبيعتها التصميمية، فلا يمكننا عادةً ربط مادة منشورة بك، وبالتالي لا يمكننا تلبية طلبات الوصول إلى البيانات أو المحو فيما يتعلق بالشهادات المجهولة المنشورة؛ أما إذا استطعت أن تثبت بصورة معقولة أنك المؤلف، أو أن المحتوى يحدد هويتك، فراجع السياسة التحريرية وسياسة الإزالة. ويحق لك تقديم شكوى إلى سلطة حماية البيانات المحلية لديك.
        </p>
        <h3>5. التخزين ونقل البيانات</h3>
        <p>
          تُستضاف البيانات داخل الاتحاد الأوروبي. وإذا راسلتنا عبر البريد الإلكتروني، فإن هذه المراسلات يتعامل معها مزود خدمة البريد الإلكتروني الخاص بنا بموجب شروطه الخاصة.
        </p>
        <h3>6. الأطفال</h3>
        <p>الموقع مخصص للبالغين (18+). ولا نقبل، عن علم، مواد مرسلة من قصّر.</p>
      </>
    ),
  },
  {
    id: "editorial",
    title: "السياسة التحريرية وسياسة الإزالة",
    effective: "تاريخ السريان: 5 يوليو 2026",
    body: (
      <>
        <h3>1. المبادئ التحريرية</h3>
        <ol>
          <li><strong>شهادات، لا أحكام.</strong> المواد المنشورة روايات شخصية تُنشر من أجل التضامن والتوعية. ولا يفصل الموقع في الوقائع.</li>
          <li><strong>إخفاء الهوية كبنية تصميمية.</strong> قبل النشر، يتحقق مراجع بشري من كل مادة وفق قائمة تدقيق ثابتة: لا أسماء أشخاص، ولا أسماء سفن أو أرقام IMO، ولا أسماء شركات، ولا تركيبات من التفاصيل يمكن أن تكشف هوية شخص أو سفينة. وتُجرى التنقيحات اللازمة وتُسجَّل.</li>
          <li><strong>الحفاظ على الصوت الأصلي.</strong> اللغة الحادة جزء من الشهادة ولا تُلطَّف؛ ولا تُحرَّر إلا التفاصيل التي تكشف الهوية. ويُحتفظ بالكلام المسيء المقتبس بوصفه شهادة.</li>
          <li><strong>النص الأصلي هو المعتمد.</strong> تتم الترجمات بمساعدة الذكاء الاصطناعي وتُعتمد بشرياً؛ وتعرض كل صفحة ترجمة اللغة الأصلية. وفي حالة الشك، يُعتد بالنص الأصلي.</li>
          <li><strong>ما نرفضه:</strong> الدعوات إلى العنف، وكشف بيانات هوية الآخرين، والتهديدات، والمحتوى الذي يحدد هوية أفراد أو سفن، والرسائل غير المرغوب فيها (spam)، والمحتوى غير المرتبط بغرض الموقع.</li>
        </ol>
        <h3>2. طلبات الإزالة والتصحيح</h3>
        <p>
          يجوز لأي شخص أن يطلب مراجعة مادة منشورة — بمن في ذلك الأشخاص الذين يعتقدون أن مادة ما تحدد هويتهم رغم ضماناتنا، والمؤلفون الذين يمكنهم إثبات تأليفهم بصورة معقولة.
        </p>
        <p>
          أرسل طلبك عبر وسيلة الاتصال في صفحة /legal (<Mail />)، متضمناً: عنوان URL الخاص بالمادة، والنص المحدد محل الاعتراض، والسبب (خطر تحديد الهوية، مخاوف بشأن الوقائع، رغبة المؤلف في السحب، أسباب قانونية).
        </p>
        <p><strong>التزاماتنا:</strong></p>
        <ul>
          <li>نؤكد الاستلام خلال <strong>7 أيام</strong>؛ ونحل المسألة خلال <strong>30 يوماً</strong> حيثما كان ذلك ممكناً؛</li>
          <li>إذا بدا أن المحتوى يحدد، بدرجة معقولة، هوية شخص حقيقي أو سفينة حقيقية، فسنقوم بحجب التفاصيل أو إلغاء النشر <strong>أولاً</strong>، ثم نناقش الأمر بعد ذلك؛</li>
          <li>يجوز للمؤلفين الذين يثبتون تأليفهم أن يطلبوا إلغاء نشر موادهم الخاصة؛ ونحترم هذه الطلبات حيثما كان التحقق ممكناً بصورة معقولة؛</li>
          <li>نحتفظ بسجل داخلي لقرارات الإزالة.</li>
        </ul>
        <h3>3. إشعار حسن النية</h3>
        <p>
          يعمل الموقع بحسن نية بوصفه أرشيفاً مراجعاً للشهادات المجهولة. ولا يستضيف الموقع، عن علم، محتوى تشهيرياً: فالمواد لا تسمي أحداً، وتحديد هوية أي شخص من مادة منشورة سيكون مخالفاً لكل من قواعدنا وعملية التحرير لدينا. إذا كنت تعتقد أن مادة ما تنتهك هذه السياسة، فأخبرنا — فلهذا وُضعت هذه العملية.
        </p>
      </>
    ),
  },
];

const DOCS_BY_LANG: Record<Lang, Doc[]> = {
  en: DOCS_EN,
  ru: DOCS_RU,
  fr: DOCS_FR,
  es: DOCS_ES,
  zh: DOCS_ZH,
  ar: DOCS_AR,
};

export function LegalContent() {
  const { lang } = useI18n();
  const docs = DOCS_BY_LANG[lang] ?? DOCS_EN;
  const labels = LABELS[lang] ?? LABELS.en;

  return (
    <section className="mx-auto max-w-[44rem] px-5 pb-16">
      <style>{`
        .legal-body h3 {
          font-family: var(--font-display, inherit);
          font-size: 1.25rem;
          line-height: 1.3;
          margin-top: 1.75rem;
          margin-bottom: 0.5rem;
          color: var(--ink);
        }
        .legal-body ul, .legal-body ol { padding-inline-start: 1.35rem; margin: 0.25rem 0 0.5rem; }
        .legal-body ul { list-style: disc; }
        .legal-body ol { list-style: decimal; }
        .legal-body li { margin: 0.4rem 0; }
        .legal-body li::marker { color: var(--amber); }
        .legal-body strong { font-weight: 600; }
        [dir="rtl"] .legal-body ul, [dir="rtl"] .legal-body ol { padding-inline-start: 1.35rem; }
      `}</style>
      <nav
        aria-label={labels.onThisPage}
        className="mb-12 rounded-sm border border-border bg-card/40 p-5"
      >
        <p className="mb-3 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
          {labels.onThisPage}
        </p>
        <ol className="space-y-1.5 text-sm">
          {docs.map((d, i) => (
            <li key={d.id} className="flex gap-3">
              <span className="tabular-nums text-muted-foreground">
                {String(i + 1).padStart(2, "0")}
              </span>
              <a
                href={`#${d.id}`}
                className="underline-offset-4 hover:underline"
                style={{ color: "var(--amber)" }}
              >
                {d.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="space-y-16">
        {docs.map((doc, i) => (
          <article
            key={doc.id}
            id={doc.id}
            className="scroll-mt-24 rounded-sm border border-border bg-card/40 p-6 sm:p-8"
          >
            <div className="mb-5 flex items-center justify-between gap-4 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              <span>Doc № {String(i + 1).padStart(4, "0")}</span>
              <span>{doc.effective}</span>
            </div>
            <div className="logbook-rule mb-6" />
            <h2 className="font-display text-3xl leading-tight sm:text-4xl">{doc.title}</h2>
            <div className="legal-body mt-6 space-y-4 text-[1.02rem] leading-[1.75]">
              {doc.body}
              <p className="pt-4 text-sm text-muted-foreground">
                {labels.contactLabel}: <Mail />
              </p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
